import React, { useState, useEffect } from "react";
import {
  XAxis,
  YAxis,
  ResponsiveContainer,
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { Download, RefreshCcw, Users } from "lucide-react";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

const LeadDashboard = ({ darkMode }) => {
  const [recentDealsFilter, setRecentDealsFilter] = useState("Last 30 Days");
  const [loaded, setLoaded] = useState(false);

  const recentDeals = [
    { name: "Anlook", company: "Anlook", phone: "8796541236", status: "Open" },
    {
      name: "Chaffo",
      company: "Chaffo F",
      phone: "78965641230",
      status: "Open",
    },
    {
      name: "Grown Pe",
      company: "Payment",
      phone: "7410258963",
      status: "Close",
    },
    {
      name: "Shubhpaisa",
      company: "Loan",
      phone: "98574123698",
      status: "Contacted",
    },
    {
      name: "Packky",
      company: "Delevery",
      phone: "1236547890",
      status: "Open",
    },
  ];

  const lostDealsData = [
    { name: "Talk", value: 480 },
    { name: "Follow Up", value: 320 },
    { name: "Inpipeline", value: 450 },
  ];

  const wonDealsData = [
    { name: "Talk", value: 380 },
    { name: "Follow", value: 180 },
    { name: "Pipe", value: 280 },
  ];

  const chartdata = [
    { name: "Jan", sales: 4000 },
    { name: "Feb", sales: 3000 },
    { name: "Mar", sales: 500 },
    { name: "Apr", sales: 4000 },
    { name: "May", sales: 2500 },
    { name: "Jun", sales: 7000 },
    { name: "July", sales: 2000 },
    { name: "Aug", sales: 6000 },
    { name: "sept", sales: 19000 },
    { name: "Oct", sales: 1000 },
    { name: "Nov", sales: 4000 },
    { name: "Dec", sales: 7000 },
  ];

  const doughnutData = {
    labels: ["Red", "Blue", "Yellow", "Green"],
    datasets: [
      {
        label: "Orders",
        data: [12, 19, 7, 5],
        backgroundColor: [
          "rgba(239, 68, 68, 0.7)",
          "rgba(59, 130, 246, 0.7)",
          "rgba(253, 224, 71, 0.7)",
          "rgba(34, 197, 94, 0.7)",
        ],
        borderColor: [
          "rgba(239, 68, 68, 1)",
          "rgba(59, 130, 246, 1)",
          "rgba(253, 224, 71, 1)",
          "rgba(34, 197, 94, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "px-2 py-1 rounded text-xs font-medium transition-colors";
    if (status === "Open")
      return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
    if (status === "Close")
      return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
    if (status === "Contacted")
      return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
    return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
  };

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 shadow rounded-2xl">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3 mb-6 md:mb-10 animate-fade-in">
          <div className="flex items-center justify-center h-12 w-12 rounded-2xl bg-blue-100 dark:bg-blue-900">
            <Users className="h-6 w-6 text-blue-500 dark:text-blue-300" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
              Lead Dashboard
            </h1>
            <p className="text-gray-900 dark:text-gray-200 font-semibold text-lg mt-1">
              1,245 Leads
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            className="border rounded px-2 py-1 bg-white dark:bg-gray-800 dark:text-gray-100"
          />
          <span className="dark:text-gray-300">to</span>
          <input
            type="date"
            className="border rounded px-2 py-1 bg-white dark:bg-gray-800 dark:text-gray-100"
          />
          <div className="flex gap-3">
            <button className="bg-gray-500 flex items-center justify-center text-white px-3 py-1 text-sm rounded hover:bg-gray-600 transition gap-2 h-8 min-w-[80px]">
              <RefreshCcw className="w-4 h-4" />
              Refresh
            </button>
            <button className="bg-green-500 flex items-center justify-center text-white px-3 py-1 text-sm rounded hover:bg-green-600 transition gap-2 h-8 min-w-[80px]">
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* First Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Left Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 flex-1 flex flex-col shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Recently Created Deals
            </h2>
            <select
              className="border rounded px-3 py-1 text-gray-600 dark:text-gray-200 dark:bg-gray-700"
              value={recentDealsFilter}
              onChange={(e) => setRecentDealsFilter(e.target.value)}
            >
              <option>Last 7 Days</option>
              <option>Last 15 Days</option>
              <option>Last 30 Days</option>
              <option>Last 60 Days</option>
            </select>
          </div>
          <div className="overflow-y-auto flex-1">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                    Lead Name
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                    Company Name
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                    Phone
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentDeals.map((deal, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-gray-100 dark:border-gray-700"
                  >
                    <td className="py-3 px-2 text-sm text-gray-900 dark:text-gray-100">
                      {deal.name}
                    </td>
                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300">
                      {deal.company}
                    </td>
                    <td className="py-3 px-2 text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {deal.phone}
                    </td>
                    <td className="py-3 px-2">
                      <span className={getStatusBadge(deal.status)}>
                        {deal.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Doughnut */}
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="w-80 h-80 mt-4">
            <Pie key={darkMode ? "dark" : "light"} data={doughnutData} />
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="w-full h-96 bg-white shadow-md rounded-lg p-4 mb-6 dark:bg-gray-800 transition-colors">
        <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-100">
          Monthly Sales
        </h2>
        <ResponsiveContainer width="100%" height="100%">
  <AreaChart
    data={chartdata}
    margin={{ top: 20, right: 30, left: 0, bottom: 30 }}
  >
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="name" />
    <YAxis />
    <Tooltip />

    <Area
      type="monotone"
      dataKey="sales"
      stroke="#4f46e5"
      strokeWidth={3}
      fill="#4f46e5"
      fillOpacity={0.3} 
    />
  </AreaChart>
</ResponsiveContainer>

      </div>

      {/* Third Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Lost Deals */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Lost Deals Stage
          </h2>
          <div className="space-y-4">
            {lostDealsData.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300 w-24">
                  {item.name}
                </span>
                <div className="flex-1 mx-4">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-6">
                    <div
                      className="bg-red-500 h-6 rounded-full transition-all duration-1000"
                      style={{
                        width: loaded ? `${(item.value / 500) * 100}%` : "0%",
                      }}
                    ></div>
                  </div>
                </div>
                <span className="text-sm text-gray-900 dark:text-gray-100 w-8">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Won Deals */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Won Deals Stage
          </h2>
          <div className="space-y-4">
            {wonDealsData.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300 w-24">
                  {item.name}
                </span>
                <div className="flex-1 mx-4">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-6">
                    <div
                      className="bg-green-500 h-6 rounded-full transition-all duration-1000"
                      style={{
                        width: loaded ? `${(item.value / 400) * 100}%` : "0%",
                      }}
                    ></div>
                  </div>
                </div>
                <span className="text-sm text-gray-900 dark:text-gray-100 w-8">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style jsx>{`
        svg:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
};

export default LeadDashboard;
